"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  PencilRuler,
  CheckCheck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Image from "next/image";

const steps = [
  {
    title: "Discover",
    content:
      "We begin by listening—understanding your lifestyle, aspirations, and the story your space should tell. This foundational step ensures every detail starts with you.",
    emoji: "📩",
    fallbackIcon: <User className="w-5 h-5 text-[#A65D5D]" />,
  },
  {
    title: "Design",
    content:
      "With your vision at the heart, we craft a custom concept—from layouts and textures to lighting and mood. Every element is curated to reflect your identity.",
    emoji: "🖌️",
    fallbackIcon: <PencilRuler className="w-5 h-5 text-[#A65D5D]" />,
  },
  {
    title: "Deliver",
    content:
      "We manage every detail with care—from construction to styling. Your dream space comes to life, elevated by precision, passion, and polish.",
    emoji: "🚚",
    fallbackIcon: <CheckCheck className="w-5 h-5 text-[#A65D5D]" />,
  },
];

export default function Process() {
  const [activeIndex, setActiveIndex] = useState(0);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? -1 : index);
  };

  return (
    <section className="bg-primaryColor-darker/60 py-20 px-4 sm:px-6 md:px-12 min-h-screen flex items-center">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* LEFT */}
        <div>
          <motion.h2
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-6xl md:text-7xl font-serif text-[#A65D5D] mb-6 leading-tight"
          >
            Crafting Elegance <br /> from Vision to Reality
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-[#6B4F4F] text-base sm:text-xl mb-10 max-w-xl"
          >
            Every great space begins with a story—yours. Our process is designed
            to bring your vision to life through thoughtful discovery, refined
            design, and seamless delivery.
          </motion.p>

          {/* Accordion */}
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div
                key={index}
                className="border border-[#E6CFCF] rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full flex justify-between items-center px-4 py-4 font-bold text-left text-[#3E2F2F]  text-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{step.emoji}</span>
                    <span>{step.title}</span>
                  </div>
                  {activeIndex === index ? (
                    <ChevronUp className="w-5 h-5 text-[#A65D5D]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#A65D5D]" />
                  )}
                </button>

                <AnimatePresence>
                  {activeIndex === index && (
                    <motion.div
                      key="content"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-4 pb-4 text-[#6B4F4F] text-md font-semibold"
                    >
                      {step.content}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* CTA line */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-10 text-[#A65D5D] text-lg sm:text-xl font-medium italic"
          >
            Let’s build your next statement of style and soul.
          </motion.p>
        </div>

        {/* RIGHT: Image */}
        <motion.div
          className="w-full"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Image
            src="/SLIDE.webp"
            alt="Process Visual"
            width={400}
            height={400}
            className="w-full h-auto rounded-lg shadow-lg object-contain"
            loading="lazy"
            quality={85}
          />
        </motion.div>
      </div>
    </section>
  );
}
